<template>
  <div :class="themeClasses.pageContainer" class="parametros-config">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h4 :class="themeClasses.headingText" class="mb-1">Parámetros del Sistema</h4>
        <p :class="themeClasses.mutedText" class="mb-0">Configura los parámetros generales del sistema</p>
      </div>
      <div class="d-flex gap-2">
        <button 
          :class="themeClasses.buttonOutlineInfo" 
          class="btn btn-outline-info" 
          @click="resetearDefaults"
          :disabled="guardando || loading"
        >
          <i class="fas fa-undo me-2"></i>
          Restaurar Defaults
        </button>
        <button 
          :class="themeClasses.buttonSuccess" 
          class="btn btn-success" 
          @click="guardarParametros"
          :disabled="guardando || loading || !hayChangios"
        >
          <i v-if="guardando" class="fas fa-spinner fa-spin me-2"></i>
          <i v-else class="fas fa-save me-2"></i>
          Guardar Cambios
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="mt-3 text-muted">Cargando parámetros del sistema...</p>
    </div>

    <!-- Contenido -->
    <div v-else class="row">
      <!-- Configuración General -->
      <div class="col-lg-8">
        <div class="accordion" id="accordionParametros">
          <!-- Configuración General -->
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button 
                class="accordion-button" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#collapseGeneral"
              >
                <i class="fas fa-cog text-primary me-2"></i>
                <strong>Configuración General</strong>
              </button>
            </h2>
            <div id="collapseGeneral" class="accordion-collapse collapse show">
              <div class="accordion-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Nombre del Sistema</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="parametros.system_name"
                        placeholder="SICECOM"
                      >
                      <div class="form-text">Nombre que aparece en el sistema</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Versión del Sistema</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="parametros.system_version"
                        placeholder="1.0.0"
                        readonly
                      >
                      <div class="form-text">Versión actual del sistema</div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Zona Horaria</label>
                      <select class="form-select" v-model="parametros.timezone">
                        <option value="America/Guayaquil">America/Guayaquil (Ecuador)</option>
                        <option value="America/Bogota">America/Bogota (Colombia)</option>
                        <option value="America/Lima">America/Lima (Perú)</option>
                        <option value="America/Caracas">America/Caracas (Venezuela)</option>
                        <option value="America/Santiago">America/Santiago (Chile)</option>
                        <option value="America/Argentina/Buenos_Aires">America/Buenos_Aires (Argentina)</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Idioma del Sistema</label>
                      <select class="form-select" v-model="parametros.language">
                        <option value="es">Español</option>
                        <option value="en">Inglés</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="parametros.maintenance_mode"
                      id="maintenanceMode"
                    >
                    <label class="form-check-label" for="maintenanceMode">
                      <strong>Modo Mantenimiento</strong>
                    </label>
                    <div class="form-text">Activa el modo mantenimiento para el sistema</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuración de Facturación -->
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button 
                class="accordion-button collapsed" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#collapseFacturacion"
              >
                <i class="fas fa-file-invoice text-success me-2"></i>
                <strong>Configuración de Facturación</strong>
              </button>
            </h2>
            <div id="collapseFacturacion" class="accordion-collapse collapse">
              <div class="accordion-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Serie de Facturación</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="parametros.invoice_series"
                        placeholder="001-001"
                        pattern="[0-9]{3}-[0-9]{3}"
                      >
                      <div class="form-text">Formato: 001-001</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Secuencial Inicial</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="parametros.invoice_sequence"
                        min="1"
                        max="999999999"
                      >
                      <div class="form-text">Número de secuencial para próxima factura</div>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">IVA por Defecto (%)</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="parametros.default_tax_rate"
                        min="0"
                        max="100"
                        step="0.01"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Moneda</label>
                      <select class="form-select" v-model="parametros.currency">
                        <option value="USD">USD - Dólar Americano</option>
                        <option value="EUR">EUR - Euro</option>
                        <option value="COP">COP - Peso Colombiano</option>
                        <option value="PEN">PEN - Sol Peruano</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="parametros.auto_send_invoice"
                      id="autoSendInvoice"
                    >
                    <label class="form-check-label" for="autoSendInvoice">
                      Envío automático de facturas por email
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuración de Email -->
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button 
                class="accordion-button collapsed" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#collapseEmail"
              >
                <i class="fas fa-envelope text-info me-2"></i>
                <strong>Configuración de Email</strong>
              </button>
            </h2>
            <div id="collapseEmail" class="accordion-collapse collapse">
              <div class="accordion-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Servidor SMTP</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model="parametros.smtp_host"
                        placeholder="smtp.gmail.com"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Puerto SMTP</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="parametros.smtp_port"
                        placeholder="587"
                      >
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Usuario SMTP</label>
                      <input
                        type="email"
                        class="form-control"
                        v-model="parametros.smtp_username"
                        placeholder="usuario@empresa.com"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Contraseña SMTP</label>
                      <input
                        type="password"
                        class="form-control"
                        v-model="parametros.smtp_password"
                        placeholder="••••••••"
                      >
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Encriptación</label>
                      <select class="form-select" v-model="parametros.smtp_encryption">
                        <option value="tls">TLS</option>
                        <option value="ssl">SSL</option>
                        <option value="none">Sin encriptación</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Email Remitente</label>
                      <input
                        type="email"
                        class="form-control"
                        v-model="parametros.from_email"
                        placeholder="noreply@empresa.com"
                      >
                    </div>
                  </div>
                </div>

                <div class="d-flex gap-2">
                  <button 
                    class="btn btn-outline-primary btn-sm"
                    @click="probarEmail"
                    :disabled="probandoEmail"
                  >
                    <i v-if="probandoEmail" class="fas fa-spinner fa-spin me-2"></i>
                    <i v-else class="fas fa-paper-plane me-2"></i>
                    Probar Configuración
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Configuración de Seguridad -->
          <div class="accordion-item">
            <h2 class="accordion-header">
              <button 
                class="accordion-button collapsed" 
                type="button" 
                data-bs-toggle="collapse" 
                data-bs-target="#collapseSecurity"
              >
                <i class="fas fa-shield-alt text-warning me-2"></i>
                <strong>Configuración de Seguridad</strong>
              </button>
            </h2>
            <div id="collapseSecurity" class="accordion-collapse collapse">
              <div class="accordion-body">
                <div class="row">
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Tiempo de Sesión (minutos)</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="parametros.session_timeout"
                        min="5"
                        max="1440"
                      >
                      <div class="form-text">Tiempo antes de cerrar sesión automáticamente</div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">Intentos de Login</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="parametros.max_login_attempts"
                        min="3"
                        max="10"
                      >
                      <div class="form-text">Intentos antes de bloquear cuenta</div>
                    </div>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="parametros.force_password_change"
                      id="forcePasswordChange"
                    >
                    <label class="form-check-label" for="forcePasswordChange">
                      Forzar cambio de contraseña en primer login
                    </label>
                  </div>
                </div>

                <div class="mb-3">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="parametros.enable_2fa"
                      id="enable2fa"
                    >
                    <label class="form-check-label" for="enable2fa">
                      Habilitar Autenticación de Dos Factores (2FA)
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel Lateral -->
      <div class="col-lg-4">
        <!-- Estado del Sistema -->
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fas fa-server text-success me-2"></i>
              Estado del Sistema
            </h6>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Estado General</span>
                <span class="badge bg-success">Operativo</span>
              </div>
            </div>
            
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Base de Datos</span>
                <span class="badge bg-success">Conectada</span>
              </div>
            </div>
            
            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Configuración Email</span>
                <span class="badge bg-warning">No Probada</span>
              </div>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <span>Modo Mantenimiento</span>
                <span class="badge" :class="parametros.maintenance_mode ? 'bg-warning' : 'bg-success'">
                  {{ parametros.maintenance_mode ? 'Activo' : 'Inactivo' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Información de Versión -->
        <div class="card mb-4">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fas fa-info-circle text-info me-2"></i>
              Información de Versión
            </h6>
          </div>
          <div class="card-body">
            <div class="mb-2">
              <strong>Versión:</strong> {{ parametros.system_version }}
            </div>
            <div class="mb-2">
              <strong>Compilación:</strong> 2025.01.15
            </div>
            <div class="mb-2">
              <strong>PHP:</strong> {{ phpVersion }}
            </div>
            <div class="mb-2">
              <strong>Base de Datos:</strong> MySQL {{ dbVersion }}
            </div>
          </div>
        </div>

        <!-- Acciones Avanzadas -->
        <div class="card">
          <div class="card-header">
            <h6 class="mb-0">
              <i class="fas fa-tools text-danger me-2"></i>
              Acciones Avanzadas
            </h6>
          </div>
          <div class="card-body">
            <div class="d-grid gap-2">
              <button 
                class="btn btn-outline-info btn-sm"
                @click="limpiarCache"
                :disabled="limpiandoCache"
              >
                <i v-if="limpiandoCache" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-trash me-2"></i>
                Limpiar Caché
              </button>
              
              <button 
                class="btn btn-outline-warning btn-sm"
                @click="exportarConfiguracion"
                :disabled="exportando"
              >
                <i v-if="exportando" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-download me-2"></i>
                Exportar Configuración
              </button>
              
              <button 
                class="btn btn-outline-primary btn-sm"
                @click="verificarActualizaciones"
                :disabled="verificandoActualizaciones"
              >
                <i v-if="verificandoActualizaciones" class="fas fa-spinner fa-spin me-2"></i>
                <i v-else class="fas fa-sync me-2"></i>
                Verificar Actualizaciones
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Alerta de cambios -->
    <div v-if="hayChangios" class="alert alert-warning d-flex align-items-center mt-4">
      <i class="fas fa-exclamation-triangle me-3"></i>
      <div class="flex-grow-1">
        <strong>Tienes cambios sin guardar.</strong>
        Los cambios se aplicarán cuando presiones "Guardar Cambios".
      </div>
      <button 
        class="btn btn-outline-warning btn-sm ms-3"
        @click="descartarCambios"
      >
        Descartar
      </button>
    </div>
  </div>
</template>

<script>
import ConfiguracionService from '@/services/configuracion'
import { Toast } from '@/utils/notifications'

import themeMixin from '@/mixins/themeMixin'

export default {
  name: 'AdminConfiguracionParametros',
  mixins: [themeMixin],
  data() {
    return {
      loading: true,
      guardando: false,
      probandoEmail: false,
      limpiandoCache: false,
      exportando: false,
      verificandoActualizaciones: false,
      
      parametros: {
        // General
        system_name: 'FACTURED',
        system_version: '1.0.0',
        timezone: 'America/Guayaquil',
        language: 'es',
        maintenance_mode: false,
        
        // Facturación
        invoice_series: '001-001',
        invoice_sequence: 1,
        default_tax_rate: 15,
        currency: 'USD',
        auto_send_invoice: false,
        
        // Email
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_encryption: 'tls',
        from_email: '',
        
        // Seguridad
        session_timeout: 120,
        max_login_attempts: 5,
        force_password_change: false,
        enable_2fa: false
      },
      
      parametrosOriginales: {},
      phpVersion: '8.1',
      dbVersion: '8.0'
    }
  },
  computed: {
    hayChangios() {
      return JSON.stringify(this.parametros) !== JSON.stringify(this.parametrosOriginales)
    }
  },
  async mounted() {
    await this.cargarParametros()
  },
  methods: {
    async cargarParametros() {
      try {
        this.loading = true
        
        const response = await ConfiguracionService.getParametros()
        
        if (response.success) {
          const { parametros, system_info } = response.data
          
          // Mapear parámetros
          Object.keys(this.parametros).forEach(key => {
            if (parametros[key] !== undefined) {
              this.parametros[key] = parametros[key]
            }
          })
          
          // Información del sistema
          if (system_info) {
            this.phpVersion = system_info.php_version || '8.1'
            this.dbVersion = system_info.db_version || '8.0'
          }
          
          // Guardar estado original
          this.parametrosOriginales = JSON.parse(JSON.stringify(this.parametros))
        } else {
          Toast.error('Error al cargar parámetros del sistema')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al cargar parámetros del sistema')
      } finally {
        this.loading = false
      }
    },

    async guardarParametros() {
      try {
        this.guardando = true
        
        const response = await ConfiguracionService.updateParametros(this.parametros)
        
        if (response.success) {
          this.parametrosOriginales = JSON.parse(JSON.stringify(this.parametros))
          Toast.success('Parámetros del sistema actualizados correctamente')
        } else {
          Toast.error(response.error || 'Error al guardar parámetros')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al guardar parámetros del sistema')
      } finally {
        this.guardando = false
      }
    },

    async probarEmail() {
      try {
        this.probandoEmail = true
        
        const response = await ConfiguracionService.probarEmail({
          smtp_host: this.parametros.smtp_host,
          smtp_port: this.parametros.smtp_port,
          smtp_username: this.parametros.smtp_username,
          smtp_password: this.parametros.smtp_password,
          smtp_encryption: this.parametros.smtp_encryption,
          from_email: this.parametros.from_email
        })
        
        if (response.success) {
          Toast.success('Configuración de email probada correctamente')
        } else {
          Toast.error(response.error || 'Error en la configuración de email')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al probar configuración de email')
      } finally {
        this.probandoEmail = false
      }
    },

    async limpiarCache() {
      try {
        this.limpiandoCache = true
        
        const response = await ConfiguracionService.limpiarCache()
        
        if (response.success) {
          Toast.success('Caché limpiado correctamente')
        } else {
          Toast.error('Error al limpiar caché')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al limpiar caché')
      } finally {
        this.limpiandoCache = false
      }
    },

    async exportarConfiguracion() {
      try {
        this.exportando = true
        
        const response = await ConfiguracionService.exportarParametros()
        
        // Crear enlace de descarga
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `parametros-sistema-${new Date().toISOString().split('T')[0]}.json`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        
        Toast.success('Configuración exportada correctamente')
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al exportar configuración')
      } finally {
        this.exportando = false
      }
    },

    async verificarActualizaciones() {
      try {
        this.verificandoActualizaciones = true
        
        const response = await ConfiguracionService.verificarActualizaciones()
        
        if (response.success) {
          if (response.data.updates_available) {
            Toast.info(`Actualizaciones disponibles: ${response.data.latest_version}`)
          } else {
            Toast.success('El sistema está actualizado')
          }
        } else {
          Toast.error('Error al verificar actualizaciones')
        }
      } catch (error) {
        console.error('Error:', error)
        Toast.error('Error al verificar actualizaciones')
      } finally {
        this.verificandoActualizaciones = false
      }
    },

    async resetearDefaults() {
      const result = await window.Swal.fire({
        title: 'Restaurar Valores por Defecto',
        text: '¿Estás seguro de restaurar los valores por defecto? Se perderán todos los cambios.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, restaurar',
        cancelButtonText: 'Cancelar'
      })

      if (!result.isConfirmed) return

      this.parametros = {
        system_name: 'FACTURED',
        system_version: '1.0.0',
        timezone: 'America/Guayaquil',
        language: 'es',
        maintenance_mode: false,
        invoice_series: '001-001',
        invoice_sequence: 1,
        default_tax_rate: 15,
        currency: 'USD',
        auto_send_invoice: false,
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_encryption: 'tls',
        from_email: '',
        session_timeout: 120,
        max_login_attempts: 5,
        force_password_change: false,
        enable_2fa: false
      }
      
      Toast.info('Valores por defecto restaurados')
    },

    descartarCambios() {
      this.parametros = JSON.parse(JSON.stringify(this.parametrosOriginales))
      Toast.info('Cambios descartados')
    }
  }
}
</script>

<style scoped>
.parametros-config {
  padding: 1.5rem;
}

.accordion-button {
  padding: 1rem 1.25rem;
}

.accordion-button:not(.collapsed) {
  color: #0c63e4;
  background-color: #e7f1ff;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
}

.form-text {
  font-size: 0.75rem;
  color: #6c757d;
}

.card {
  border: 1px solid #dee2e6;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.alert {
  border-radius: 0.375rem;
}

@media (max-width: 768px) {
  .parametros-config {
    padding: 1rem;
  }
  
  .col-lg-4,
  .col-lg-8 {
    margin-bottom: 1rem;
  }
}
</style>
